import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCopy, FiCheck, FiSend, FiExternalLink } from "react-icons/fi";
import { Modal } from "../../Components/Modal";
import { PUBLIC_API_BASE_URL } from "../../Helper/Constant";
import {
	externalApiClient,
	type ExternalApiToken,
	type ExternalApiLog,
	type ExternalApiStats,
	type CreateTokenResponse,
} from "./externalApiClient";

/**
 * Admin page for the internal content API (articles), consumed by internal/partner
 * services : never by site end-users.
 *
 *  - Domain panel: the API base URL, the available endpoints and the auth header.
 *  - Tester: paste a `pk_live_…` token and call the two read endpoints live, with the
 *    JSON response rendered in a formatted, copyable console (à la "try it").
 *  - Tokens: mint named read-only tokens (plaintext shown once) and revoke them.
 *  - Logs & usage: aggregate counts + a recent-calls table from the request log.
 */

function fmtDate(s?: string | null): string {
	if (!s) return "-";
	const d = new Date(s);
	return isNaN(d.getTime()) ? "-" : d.toLocaleString("fr-FR");
}

/** Renders pretty-printed JSON with light token colouring as React nodes.
 * No dangerouslySetInnerHTML : React escapes every text node, so there is no XSS
 * surface even though the body comes from a remote API response. */
const JsonView: React.FC<{ json: string }> = ({ json }) => {
	const re = /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;
	const nodes: React.ReactNode[] = [];
	let last = 0;
	let key = 0;
	let m: RegExpExecArray | null;
	while ((m = re.exec(json)) !== null) {
		if (m.index > last) nodes.push(json.slice(last, m.index));
		const tok = m[0];
		let cls = "text-emerald-300"; // number
		if (tok.startsWith('"')) cls = /:$/.test(tok) ? "text-sky-300" : "text-amber-200"; // key vs string
		else if (tok === "true" || tok === "false") cls = "text-fuchsia-300";
		else if (tok === "null") cls = "text-slate-400";
		nodes.push(<span key={key++} className={cls}>{tok}</span>);
		last = m.index + tok.length;
	}
	if (last < json.length) nodes.push(json.slice(last));
	return <>{nodes}</>;
};

const CopyButton: React.FC<{ value: string; label?: string }> = ({ value, label }) => {
	const [done, setDone] = useState(false);
	return (
		<button
			type="button"
			onClick={async () => {
				try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* ignore */ }
			}}
			className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:border-slate-400 hover:bg-slate-50"
			title="Copier"
		>
			{done ? <FiCheck className="text-emerald-600" /> : <FiCopy />}
			{label && <span>{done ? "Copié" : label}</span>}
		</button>
	);
};

type TestResult = {
	status: number;
	ok: boolean;
	ms: number;
	url: string;
	body: string; // pretty JSON or raw text
} | null;

/** A single "try it" panel: builds a request to the content API with the shared token,
 * sends it, and renders the formatted response. */
const TesterPanel: React.FC<{
	title: string;
	description: string;
	method: string;
	token: string;
	buildPath: (state: Record<string, string>) => string;
	fields: { key: string; label: string; placeholder?: string; width?: string }[];
	initial: Record<string, string>;
}> = ({ title, description, method, token, buildPath, fields, initial }) => {
	const [state, setState] = useState<Record<string, string>>(initial);
	const [busy, setBusy] = useState(false);
	const [result, setResult] = useState<TestResult>(null);
	const [err, setErr] = useState<string | null>(null);

	const path = useMemo(() => buildPath(state), [buildPath, state]);
	const fullUrl = `${PUBLIC_API_BASE_URL}${path}`;

	const send = async () => {
		setErr(null);
		if (!token.trim()) { setErr("Renseignez un jeton pk_live_… ci-dessus."); return; }
		setBusy(true);
		const started = performance.now();
		try {
			const resp = await fetch(fullUrl, {
				method,
				headers: { Authorization: `Bearer ${token.trim()}`, Accept: "application/json" },
			});
			const text = await resp.text();
			let body = text;
			try { body = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
			setResult({ status: resp.status, ok: resp.ok, ms: Math.round(performance.now() - started), url: fullUrl, body });
		} catch (e: any) {
			setErr(e?.message || "Échec de la requête (réseau ou CORS).");
			setResult(null);
		} finally {
			setBusy(false);
		}
	};

	const statusTone = (code: number) =>
		code >= 500 ? "bg-rose-100 text-rose-700" : code >= 400 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";

	return (
		<div className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
			<div className="border-b border-slate-100 px-5 py-4">
				<div className="flex items-center gap-2">
					<span className="rounded-md bg-slate-900 px-2 py-0.5 font-mono text-[11px] font-semibold uppercase text-white">{method}</span>
					<h3 className="text-sm font-semibold text-slate-800">{title}</h3>
				</div>
				<p className="mt-1 text-xs text-slate-500">{description}</p>
			</div>

			<div className="space-y-3 px-5 py-4">
				<div className="flex flex-wrap items-end gap-3">
					{fields.map((f) => (
						<div key={f.key} className={f.width ?? "flex-1 min-w-[8rem]"}>
							<label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-slate-500">{f.label}</label>
							<input
								value={state[f.key] ?? ""}
								placeholder={f.placeholder}
								onChange={(e) => setState((s) => ({ ...s, [f.key]: e.target.value }))}
								className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
							/>
						</div>
					))}
					<button
						type="button"
						onClick={() => void send()}
						disabled={busy}
						className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
					>
						<FiSend /> {busy ? "Envoi…" : "Envoyer"}
					</button>
				</div>

				{/* Request line */}
				<div className="flex items-center gap-2 overflow-hidden rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
					<code className="flex-1 truncate font-mono text-xs text-slate-600" title={fullUrl}>{path}</code>
					<CopyButton value={fullUrl} />
				</div>

				{err && <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{err}</div>}

				{/* Response console */}
				{result && (
					<div className="overflow-hidden rounded-lg border border-slate-800 bg-slate-900">
						<div className="flex items-center gap-2 border-b border-slate-800 px-3 py-2">
							<span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${statusTone(result.status)}`}>HTTP {result.status}</span>
							<span className="text-[11px] text-slate-400">{result.ms} ms</span>
							<div className="ml-auto"><CopyButton value={result.body} label="Copier JSON" /></div>
						</div>
						<pre className="max-h-96 overflow-auto px-4 py-3 font-mono text-xs leading-relaxed text-slate-200">
							<JsonView json={result.body} />
						</pre>
					</div>
				)}
			</div>
		</div>
	);
};

const ExternalApiPage: React.FC = () => {
	const [tokens, setTokens] = useState<ExternalApiToken[]>([]);
	const [stats, setStats] = useState<ExternalApiStats | null>(null);
	const [logs, setLogs] = useState<ExternalApiLog[]>([]);
	const [logTotal, setLogTotal] = useState(0);
	const [logPage, setLogPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Create modal.
	const [createOpen, setCreateOpen] = useState(false);
	const [newName, setNewName] = useState("");
	const [newExpiry, setNewExpiry] = useState<string>(""); // days, blank = never
	const [saving, setSaving] = useState(false);
	const [created, setCreated] = useState<CreateTokenResponse | null>(null);
	const [copied, setCopied] = useState(false);

	// Revoke confirm.
	const [revokeTarget, setRevokeTarget] = useState<ExternalApiToken | null>(null);

	// Tester shared token.
	const [testToken, setTestToken] = useState("");

	const LOG_PAGE_SIZE = 50;

	const refresh = useCallback(async (page = 1) => {
		setLoading(true);
		setError(null);
		try {
			const [tk, st, lg] = await Promise.all([
				externalApiClient.listTokens(),
				externalApiClient.stats(),
				externalApiClient.logs(page, LOG_PAGE_SIZE),
			]);
			setTokens(tk);
			setStats(st);
			setLogs(lg.items);
			setLogTotal(lg.total);
			setLogPage(lg.page);
		} catch (e: any) {
			setError(e?.message || "Échec du chargement.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { void refresh(1); }, [refresh]);

	const openCreate = () => {
		setNewName("");
		setNewExpiry("");
		setCreated(null);
		setCopied(false);
		setCreateOpen(true);
	};

	const submitCreate = async () => {
		if (!newName.trim()) { setError("Le nom du jeton est requis."); return; }
		setSaving(true);
		setError(null);
		try {
			const days = newExpiry.trim() ? Number(newExpiry.trim()) : null;
			const res = await externalApiClient.createToken(newName.trim(), Number.isFinite(days as number) ? days : null);
			setCreated(res); // reveal plaintext once
			await refresh(1);
		} catch (e: any) {
			setError(e?.message || "Échec de la création.");
		} finally {
			setSaving(false);
		}
	};

	const confirmRevoke = async () => {
		if (!revokeTarget) return;
		try {
			await externalApiClient.revokeToken(revokeTarget.id);
			setRevokeTarget(null);
			await refresh(logPage);
		} catch (e: any) {
			setError(e?.message || "Échec de la révocation.");
		}
	};

	const copyToken = async () => {
		if (!created) return;
		try { await navigator.clipboard.writeText(created.token); setCopied(true); } catch { /* ignore */ }
	};

	const useCreatedInTester = () => {
		if (!created) return;
		setTestToken(created.token);
		setCreateOpen(false);
	};

	const statusTone = (code: number) =>
		code >= 500 ? "text-rose-700" : code >= 400 ? "text-amber-700" : "text-emerald-700";

	const logPages = Math.max(1, Math.ceil(logTotal / LOG_PAGE_SIZE));

	const endpoints = [
		{ method: "GET", path: "/v1/search/articles?q=&page=1&pageSize=20", note: "Recherche / liste paginée (max 20)" },
		{ method: "GET", path: "/v1/articles/{idOrSlug}", note: "Un article par id ou slug" },
		{ method: "GET", path: "/health", note: "Sonde de disponibilité (sans jeton)" },
	];

	return (
		<div className="min-h-full p-6">
			<div className="mx-auto w-full max-w-6xl space-y-8">
				{/* Header */}
				<div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
					<div>
						<p className="text-xs uppercase tracking-widest text-slate-500">Administration</p>
						<h1 className="text-2xl font-semibold text-slate-900">API interne</h1>
						<p className="mt-1 text-sm text-slate-500">
							Articles en lecture seule pour les services internes : domaine, jetons d’accès, testeur et journal des appels.
						</p>
					</div>
					<div className="ml-auto flex gap-2">
						<button
							onClick={() => void refresh(logPage)}
							className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400"
						>
							Actualiser
						</button>
						<button
							onClick={openCreate}
							className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
						>
							Générer un jeton
						</button>
					</div>
				</div>

				{error && (
					<div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
				)}

				{/* Domain / endpoints panel */}
				<section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
					<div className="border-b border-slate-100 px-5 py-3">
						<h2 className="text-sm font-semibold text-slate-700">Domaine &amp; points d’accès</h2>
					</div>
					<div className="space-y-4 px-5 py-4">
						<div>
							<p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">URL de base</p>
							<div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
								<code className="flex-1 truncate font-mono text-sm text-slate-800">{PUBLIC_API_BASE_URL}</code>
								<a
									href={`${PUBLIC_API_BASE_URL}/health`}
									target="_blank"
									rel="noreferrer"
									className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:border-slate-400 hover:bg-slate-50"
								>
									<FiExternalLink /> Santé
								</a>
								<CopyButton value={PUBLIC_API_BASE_URL} />
							</div>
						</div>

						<div>
							<p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-500">Authentification</p>
							<div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
								<code className="flex-1 truncate font-mono text-sm text-slate-800">Authorization: Bearer pk_live_…</code>
								<CopyButton value="Authorization: Bearer pk_live_" />
							</div>
						</div>

						<div>
							<p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-500">Points d’accès</p>
							<ul className="divide-y divide-slate-100 rounded-md border border-slate-200">
								{endpoints.map((e) => (
									<li key={e.path} className="flex items-center gap-3 px-3 py-2">
										<span className="rounded bg-slate-900 px-2 py-0.5 font-mono text-[11px] font-semibold uppercase text-white">{e.method}</span>
										<code className="font-mono text-xs text-slate-700">{e.path}</code>
										<span className="ml-auto text-xs text-slate-400">{e.note}</span>
									</li>
								))}
							</ul>
						</div>
					</div>
				</section>

				{/* Interactive tester */}
				<section className="space-y-4">
					<div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
						<div className="flex items-center justify-between">
							<h2 className="text-sm font-semibold text-slate-700">Tester l’API</h2>
							{tokens.length === 0 && (
								<span className="text-xs text-slate-400">Générez d’abord un jeton ci-dessous.</span>
							)}
						</div>
						<label className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Jeton (pk_live_…)</label>
						<input
							value={testToken}
							onChange={(e) => setTestToken(e.target.value)}
							placeholder="Collez un jeton pour autoriser les requêtes de test"
							className="w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm focus:border-slate-500 focus:outline-none"
						/>
						<p className="text-xs text-slate-400">
							Le jeton n’est utilisé que dans votre navigateur pour signer les requêtes de test envoyées à l’API.
						</p>
					</div>

					<div className="grid gap-4 lg:grid-cols-2">
						<TesterPanel
							title="Rechercher des articles"
							description="Liste paginée d’articles de production (recherche plein-texte facultative, 20 max par page)."
							method="GET"
							token={testToken}
							fields={[
								{ key: "q", label: "Recherche (q)", placeholder: "ex. banque", width: "flex-1 min-w-[10rem]" },
								{ key: "page", label: "Page", width: "w-20" },
								{ key: "pageSize", label: "Taille", width: "w-20" },
							]}
							initial={{ q: "", page: "1", pageSize: "20" }}
							buildPath={(s) => {
								const p = new URLSearchParams();
								if (s.q?.trim()) p.set("q", s.q.trim());
								if (s.page?.trim()) p.set("page", s.page.trim());
								if (s.pageSize?.trim()) p.set("pageSize", s.pageSize.trim());
								const qs = p.toString();
								return `/v1/search/articles${qs ? `?${qs}` : ""}`;
							}}
						/>
						<TesterPanel
							title="Obtenir un article"
							description="Un article complet (avec le corps en HTML simple) par identifiant ou slug."
							method="GET"
							token={testToken}
							fields={[
								{ key: "idOrSlug", label: "Id ou slug", placeholder: "ex. afis-2025", width: "flex-1 min-w-[12rem]" },
							]}
							initial={{ idOrSlug: "" }}
							buildPath={(s) => `/v1/articles/${encodeURIComponent(s.idOrSlug?.trim() || "")}`}
						/>
					</div>
				</section>

				{/* Stats */}
				{stats && (
					<section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
						{[
							{ label: "Appels (total)", value: stats.total },
							{ label: "24 h", value: stats.last24h },
							{ label: "7 jours", value: stats.last7d },
							{ label: "30 jours", value: stats.last30d },
							{ label: "Jetons actifs", value: stats.activeTokens },
							{ label: "2xx", value: stats.ok },
							{ label: "4xx", value: stats.clientErrors },
							{ label: "5xx", value: stats.serverErrors },
						].map((c) => (
							<div key={c.label} className="rounded-2xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
								<p className="text-[11px] uppercase tracking-widest text-slate-500">{c.label}</p>
								<p className="mt-1 text-2xl font-semibold text-slate-900">{c.value}</p>
							</div>
						))}
					</section>
				)}

				{/* Tokens */}
				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<div className="border-b border-slate-100 px-5 py-3">
						<h2 className="text-sm font-semibold text-slate-700">Jetons ({tokens.length})</h2>
					</div>
					<table className="min-w-full divide-y divide-slate-100 text-sm">
						<thead className="bg-slate-50">
							<tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
								<th className="px-5 py-3 text-left">Nom</th>
								<th className="px-5 py-3 text-left">Préfixe</th>
								<th className="px-5 py-3 text-left">État</th>
								<th className="px-5 py-3 text-left">Créé</th>
								<th className="px-5 py-3 text-left">Expire</th>
								<th className="px-5 py-3 text-left">Dernier usage</th>
								<th className="px-5 py-3 text-right">Action</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{tokens.length === 0 && (
								<tr><td colSpan={7} className="px-5 py-6 text-center text-slate-400">{loading ? "Chargement…" : "Aucun jeton."}</td></tr>
							)}
							{tokens.map((t) => (
								<tr key={t.id} className="hover:bg-slate-50">
									<td className="px-5 py-3 font-medium text-slate-800">{t.name}</td>
									<td className="px-5 py-3 font-mono text-xs text-slate-500">{t.prefix}…</td>
									<td className="px-5 py-3">
										{t.isLive ? (
											<span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Actif</span>
										) : (
											<span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
												{t.revokedAt ? "Révoqué" : "Expiré"}
											</span>
										)}
									</td>
									<td className="px-5 py-3 text-slate-500">{fmtDate(t.createdAt)}</td>
									<td className="px-5 py-3 text-slate-500">{t.expiresAt ? fmtDate(t.expiresAt) : "Jamais"}</td>
									<td className="px-5 py-3 text-slate-500">{fmtDate(t.lastUsedAt)}</td>
									<td className="px-5 py-3 text-right">
										{t.isLive && (
											<button onClick={() => setRevokeTarget(t)} className="text-xs font-medium text-rose-700 hover:underline">
												Révoquer
											</button>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>

				{/* Logs */}
				<section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
					<div className="flex items-center border-b border-slate-100 px-5 py-3">
						<h2 className="text-sm font-semibold text-slate-700">Journal des appels</h2>
						<div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
							<button
								disabled={logPage <= 1}
								onClick={() => void refresh(logPage - 1)}
								className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
							>‹</button>
							<span>{logPage} / {logPages}</span>
							<button
								disabled={logPage >= logPages}
								onClick={() => void refresh(logPage + 1)}
								className="rounded border border-slate-300 px-2 py-1 disabled:opacity-40"
							>›</button>
						</div>
					</div>
					<table className="min-w-full divide-y divide-slate-100 text-sm">
						<thead className="bg-slate-50">
							<tr className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
								<th className="px-5 py-3 text-left">Date</th>
								<th className="px-5 py-3 text-left">Jeton</th>
								<th className="px-5 py-3 text-left">Méthode</th>
								<th className="px-5 py-3 text-left">Chemin</th>
								<th className="px-5 py-3 text-right">Statut</th>
								<th className="px-5 py-3 text-right">Durée</th>
								<th className="px-5 py-3 text-left">IP</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-slate-100">
							{logs.length === 0 && (
								<tr><td colSpan={7} className="px-5 py-6 text-center text-slate-400">{loading ? "Chargement…" : "Aucun appel."}</td></tr>
							)}
							{logs.map((l) => (
								<tr key={l.id} className="hover:bg-slate-50">
									<td className="px-5 py-3 text-slate-500">{fmtDate(l.createdAt)}</td>
									<td className="px-5 py-3 text-slate-600">{l.tokenName ?? "-"}</td>
									<td className="px-5 py-3 font-mono text-xs text-slate-500">{l.method}</td>
									<td className="px-5 py-3 font-mono text-xs text-slate-600">
										{l.path}{l.queryString ? `?${l.queryString}` : ""}
									</td>
									<td className={`px-5 py-3 text-right font-medium ${statusTone(l.statusCode)}`}>{l.statusCode}</td>
									<td className="px-5 py-3 text-right text-slate-500">{l.durationMs} ms</td>
									<td className="px-5 py-3 text-slate-500">{l.ipAddress ?? "-"}</td>
								</tr>
							))}
						</tbody>
					</table>
				</section>
			</div>

			{/* Create / reveal modal */}
			<Modal show={createOpen} onHide={() => setCreateOpen(false)} size="lg" centered>
				<Modal.Header closeButton><Modal.Title>Générer un jeton</Modal.Title></Modal.Header>
				<Modal.Body className="space-y-4">
					{!created ? (
						<>
							<div>
								<label className="mb-1 block text-sm font-medium text-slate-700">Nom du consommateur</label>
								<input
									value={newName}
									onChange={(e) => setNewName(e.target.value)}
									placeholder="ex. application mobile"
									className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								/>
							</div>
							<div>
								<label className="mb-1 block text-sm font-medium text-slate-700">Expiration (jours)</label>
								<input
									value={newExpiry}
									onChange={(e) => setNewExpiry(e.target.value.replace(/[^0-9]/g, ""))}
									placeholder="laisser vide = jamais"
									className="w-40 rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
								/>
							</div>
						</>
					) : (
						<div className="space-y-3">
							<div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
								Copiez ce jeton maintenant : il ne sera plus jamais affiché.
							</div>
							<div className="flex items-center gap-2">
								<code className="flex-1 break-all rounded-md border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800">
									{created.token}
								</code>
								<button onClick={copyToken} className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800">
									{copied ? "Copié" : "Copier"}
								</button>
							</div>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer>
					{!created ? (
						<>
							<button onClick={() => setCreateOpen(false)} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400">Annuler</button>
							<button onClick={() => void submitCreate()} disabled={saving} className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50">
								{saving ? "Création…" : "Créer"}
							</button>
						</>
					) : (
						<>
							<button onClick={useCreatedInTester} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400">Utiliser dans le testeur</button>
							<button onClick={() => setCreateOpen(false)} className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800">Terminé</button>
						</>
					)}
				</Modal.Footer>
			</Modal>

			{/* Revoke confirm */}
			<Modal show={!!revokeTarget} onHide={() => setRevokeTarget(null)} size="md" centered>
				<Modal.Header closeButton><Modal.Title>Révoquer le jeton</Modal.Title></Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-600">
						Révoquer <strong>{revokeTarget?.name}</strong> ({revokeTarget?.prefix}…) ? Les services qui l’utilisent
						recevront immédiatement une erreur 401. Cette action est irréversible.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button onClick={() => setRevokeTarget(null)} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:border-slate-400">Annuler</button>
					<button onClick={() => void confirmRevoke()} className="rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700">Révoquer</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default ExternalApiPage;
